import { useThree } from '@react-three/fiber'
import { type RefObject, useCallback } from 'react'
import * as THREE from 'three'
import type { AvatarPresentation } from '../types'

/** 画面中央に配置する際の基準比率 */
const CENTER_ANCHOR_RATIO = 0.5
/** 左右端寄せ時に画面端から確保する余白のピクセル値 */
const EDGE_PADDING_PX = 120

/** X 座標の探索開始時に中心から確保する検索範囲の半幅 */
const TARGET_X_SOLVE_RANGE_HALF = 32
/** X 座標の逆算で収束させるための二分探索回数 */
const TARGET_X_SOLVE_ITERATIONS = 24
/** 目標位置が範囲外のときに探索範囲を広げる回数 */
const TARGET_X_SOLVE_EXPAND_STEPS = 6

type UseResponsiveAvatarPlacementParams = {
  avatarPresentation?: AvatarPresentation
  groupRef: RefObject<THREE.Group | null>
}

const isFiniteNumber = (value: number) => Number.isFinite(value)

const projectPixelYForWorldY = (
  camera: THREE.Camera,
  worldX: number,
  worldY: number,
  worldZ: number,
  viewportHeight: number
): number | null => {
  const projected = new THREE.Vector3(worldX, worldY, worldZ).project(camera)
  if (!isFiniteNumber(projected.y)) return null
  return (1 - projected.y) * 0.5 * viewportHeight
}

const projectPixelXForWorldX = (
  camera: THREE.Camera,
  worldX: number,
  worldY: number,
  worldZ: number,
  viewportWidth: number
): number | null => {
  const projected = new THREE.Vector3(worldX, worldY, worldZ).project(camera)
  if (!isFiniteNumber(projected.x)) return null
  return (projected.x + 1) * 0.5 * viewportWidth
}

const solveWorldXForPixelX = (
  camera: THREE.Camera,
  baseX: number,
  worldY: number,
  worldZ: number,
  desiredPixelX: number,
  viewportWidth: number
): number | null => {
  if (!isFiniteNumber(desiredPixelX)) return null
  if (!(viewportWidth > 0)) return null

  const evalPixel = (worldX: number) => projectPixelXForWorldX(camera, worldX, worldY, worldZ, viewportWidth)

  let lowX = baseX - TARGET_X_SOLVE_RANGE_HALF
  let highX = baseX + TARGET_X_SOLVE_RANGE_HALF
  let lowPixel = evalPixel(lowX)
  let highPixel = evalPixel(highX)
  if (lowPixel == null || highPixel == null) return null

  let increasing = lowPixel < highPixel
  let minPixel = increasing ? lowPixel : highPixel
  let maxPixel = increasing ? highPixel : lowPixel

  for (let i = 0; i < TARGET_X_SOLVE_EXPAND_STEPS && (desiredPixelX < minPixel || desiredPixelX > maxPixel); i += 1) {
    const span = highX - lowX
    lowX -= span
    highX += span

    const nextLow = evalPixel(lowX)
    const nextHigh = evalPixel(highX)
    if (nextLow == null || nextHigh == null) return null

    lowPixel = nextLow
    highPixel = nextHigh
    increasing = lowPixel < highPixel
    minPixel = increasing ? lowPixel : highPixel
    maxPixel = increasing ? highPixel : lowPixel
  }

  if (desiredPixelX <= minPixel) return increasing ? lowX : highX
  if (desiredPixelX >= maxPixel) return increasing ? highX : lowX

  for (let i = 0; i < TARGET_X_SOLVE_ITERATIONS; i += 1) {
    const midX = (lowX + highX) * 0.5
    const midPixel = evalPixel(midX)
    if (midPixel == null) return null

    if (increasing ? midPixel < desiredPixelX : midPixel > desiredPixelX) {
      lowX = midX
    } else {
      highX = midX
    }
  }

  const solved = (lowX + highX) * 0.5
  return isFiniteNumber(solved) ? solved : null
}

const solveWorldYForPixelY = (
  camera: THREE.Camera,
  worldX: number,
  baseY: number,
  worldZ: number,
  desiredPixelY: number,
  viewportHeight: number
): number | null => {
  if (!isFiniteNumber(desiredPixelY)) return null
  if (!(viewportHeight > 0)) return null

  const evalPixel = (worldY: number) => projectPixelYForWorldY(camera, worldX, worldY, worldZ, viewportHeight)

  let lowY = baseY - TARGET_X_SOLVE_RANGE_HALF
  let highY = baseY + TARGET_X_SOLVE_RANGE_HALF
  let lowPixel = evalPixel(lowY)
  let highPixel = evalPixel(highY)
  if (lowPixel == null || highPixel == null) return null

  let increasing = lowPixel < highPixel
  let minPixel = increasing ? lowPixel : highPixel
  let maxPixel = increasing ? highPixel : lowPixel

  for (let i = 0; i < TARGET_X_SOLVE_EXPAND_STEPS && (desiredPixelY < minPixel || desiredPixelY > maxPixel); i += 1) {
    const span = highY - lowY
    lowY -= span
    highY += span

    const nextLow = evalPixel(lowY)
    const nextHigh = evalPixel(highY)
    if (nextLow == null || nextHigh == null) return null

    lowPixel = nextLow
    highPixel = nextHigh
    increasing = lowPixel < highPixel
    minPixel = increasing ? lowPixel : highPixel
    maxPixel = increasing ? highPixel : lowPixel
  }

  if (desiredPixelY <= minPixel) return increasing ? lowY : highY
  if (desiredPixelY >= maxPixel) return increasing ? highY : lowY

  for (let i = 0; i < TARGET_X_SOLVE_ITERATIONS; i += 1) {
    const midY = (lowY + highY) * 0.5
    const midPixel = evalPixel(midY)
    if (midPixel == null) return null

    if (increasing ? midPixel < desiredPixelY : midPixel > desiredPixelY) {
      lowY = midY
    } else {
      highY = midY
    }
  }

  const solved = (lowY + highY) * 0.5
  return isFiniteNumber(solved) ? solved : null
}

const avatarBoundsCenter = new THREE.Vector3()
const worldScratch = new THREE.Vector3()
const localScratch = new THREE.Vector3()

type AnchorLocalPoints = {
  top: THREE.Vector3
  center: THREE.Vector3
  bottom: THREE.Vector3
}

type AnchorCacheEntry = {
  points: AnchorLocalPoints
}

const anchorPointCache = new WeakMap<THREE.Group, AnchorCacheEntry>()

const CENTER_BONE_CANDIDATES = ['hips', 'pelvis', 'spine']

const normalizeName = (name: string) => name.replace(/[^a-z0-9]/gi, '').toLowerCase()

const collectBones = (group: THREE.Group): THREE.Bone[] => {
  const bones: THREE.Bone[] = []
  group.traverse((object) => {
    if (object instanceof THREE.Bone) {
      bones.push(object)
    }
  })
  return bones
}

const findBoneByKeywords = (bones: THREE.Bone[], candidates: string[]): THREE.Bone | null => {
  for (const candidate of candidates) {
    const found = bones.find((bone) => normalizeName(bone.name).includes(candidate))
    if (found) return found
  }
  return null
}

const resolveLocalPointFromBone = (group: THREE.Group, bone: THREE.Bone): THREE.Vector3 => {
  bone.getWorldPosition(worldScratch)
  localScratch.copy(worldScratch)
  return group.worldToLocal(localScratch.clone())
}

const localBoundsBox = new THREE.Box3()
const localBoundsMin = new THREE.Vector3(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY)
const localBoundsMax = new THREE.Vector3(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY)

const resolveStableLocalBounds = (group: THREE.Group): THREE.Box3 | null => {
  const inverseGroupWorldMatrix = group.matrixWorld.clone().invert()
  let hasMeshBounds = false

  localBoundsMin.set(Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY, Number.POSITIVE_INFINITY)
  localBoundsMax.set(Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY, Number.NEGATIVE_INFINITY)

  group.traverse((object) => {
    if (!(object instanceof THREE.Mesh)) return

    const geometry = object.geometry
    if (!geometry) return
    if (!geometry.boundingBox) {
      geometry.computeBoundingBox()
    }
    if (!geometry.boundingBox) return

    const localObjectMatrix = inverseGroupWorldMatrix.clone().multiply(object.matrixWorld)
    localBoundsBox.copy(geometry.boundingBox).applyMatrix4(localObjectMatrix)

    localBoundsMin.min(localBoundsBox.min)
    localBoundsMax.max(localBoundsBox.max)
    hasMeshBounds = true
  })

  if (!hasMeshBounds) return null
  if (!Number.isFinite(localBoundsMin.y) || !Number.isFinite(localBoundsMax.y)) return null

  return new THREE.Box3(localBoundsMin.clone(), localBoundsMax.clone())
}

const resolveFallbackLocalPoints = (group: THREE.Group): AnchorLocalPoints | null => {
  const stableBounds = resolveStableLocalBounds(group)
  if (!stableBounds || stableBounds.isEmpty()) {
    return null
  }

  stableBounds.getCenter(avatarBoundsCenter)

  const topWorld = new THREE.Vector3(avatarBoundsCenter.x, stableBounds.max.y, avatarBoundsCenter.z)
  const centerWorld = avatarBoundsCenter.clone()
  const bottomWorld = new THREE.Vector3(avatarBoundsCenter.x, stableBounds.min.y, avatarBoundsCenter.z)

  return {
    top: topWorld,
    center: centerWorld,
    bottom: bottomWorld,
  }
}

const resolveAnchorLocalPoints = (group: THREE.Group): AnchorLocalPoints | null => {
  const cached = anchorPointCache.get(group)
  if (cached) {
    return cached.points
  }

  const fallback = resolveFallbackLocalPoints(group)
  if (!fallback) {
    return null
  }
  const bones = collectBones(group)

  const centerBone = findBoneByKeywords(bones, CENTER_BONE_CANDIDATES)

  const points: AnchorLocalPoints = {
    top: fallback.top,
    center: centerBone ? resolveLocalPointFromBone(group, centerBone) : fallback.center,
    bottom: fallback.bottom,
  }

  anchorPointCache.set(group, {
    points,
  })

  return points
}

export const resolveAvatarAnchorWorldPoint = (
  group: THREE.Group,
  avatarAnchor: AvatarPresentation['placement']['avatarAnchor']
): THREE.Vector3 | null => {
  const localPoints = resolveAnchorLocalPoints(group)
  if (!localPoints) return null
  const localPoint =
    avatarAnchor === 'top' ? localPoints.top : avatarAnchor === 'bottom' ? localPoints.bottom : localPoints.center
  const worldPoint = group.localToWorld(localPoint.clone())
  if (!isFiniteNumber(worldPoint.y)) return null
  return worldPoint
}

export const resolveDesiredAvatarAnchorPixelY = ({
  avatarAnchor,
  offsetY,
  viewportHeight,
  unit,
}: {
  avatarAnchor: AvatarPresentation['placement']['avatarAnchor']
  offsetY: number
  viewportHeight: number
  unit: AvatarPresentation['placement']['unit']
}): number => {
  const basePixelY =
    avatarAnchor === 'top' ? 0 : avatarAnchor === 'bottom' ? viewportHeight : viewportHeight * CENTER_ANCHOR_RATIO
  const offsetPixelY = unit === 'viewport' ? offsetY * viewportHeight : offsetY
  return basePixelY + offsetPixelY
}

export const resolveAvatarAnchorOffsetY = ({
  camera,
  group,
  avatarAnchor,
  offsetY,
  viewportHeight,
  unit,
}: {
  camera: THREE.Camera
  group: THREE.Group
  avatarAnchor: AvatarPresentation['placement']['avatarAnchor']
  offsetY: number
  viewportHeight: number
  unit: AvatarPresentation['placement']['unit']
}): number | null => {
  const anchorWorldPoint = resolveAvatarAnchorWorldPoint(group, avatarAnchor)
  if (!anchorWorldPoint) return null

  const desiredPixelY = resolveDesiredAvatarAnchorPixelY({ avatarAnchor, offsetY, viewportHeight, unit })
  const solvedAnchorY = solveWorldYForPixelY(
    camera,
    anchorWorldPoint.x,
    anchorWorldPoint.y,
    anchorWorldPoint.z,
    desiredPixelY,
    viewportHeight
  )
  if (solvedAnchorY == null) return null

  return group.position.y + (solvedAnchorY - anchorWorldPoint.y)
}

export const useResponsiveAvatarPlacement = ({ avatarPresentation, groupRef }: UseResponsiveAvatarPlacementParams) => {
  const { camera, size } = useThree()

  return useCallback(
    (nextAvatarPresentation: AvatarPresentation | undefined = avatarPresentation) => {
      if (!nextAvatarPresentation) return undefined

      const placement = nextAvatarPresentation.placement
      const offset = placement.offset
      const baseX = 0
      const baseZ = Number.isFinite(offset.z ?? 0) ? (offset.z ?? 0) : 0

      const viewportWidth = Math.max(size.width, 1)
      const viewportHeight = Math.max(size.height, 1)
      if (!(viewportWidth > 0 && viewportHeight > 0)) return nextAvatarPresentation

      const offsetX = Number.isFinite(offset.x) ? offset.x : 0
      const offsetY = Number.isFinite(offset.y) ? offset.y : 0
      const offsetPixelX =
        placement.unit === 'viewport' ? offsetX * viewportWidth : placement.unit === 'px' ? offsetX : 0

      const desiredPixelX =
        placement.viewportAnchor === 'left'
          ? EDGE_PADDING_PX + offsetPixelX
          : placement.viewportAnchor === 'right'
            ? viewportWidth - EDGE_PADDING_PX + offsetPixelX
            : viewportWidth * CENTER_ANCHOR_RATIO + offsetPixelX

      const group = groupRef.current
      if (!group) return undefined

      const solvedX =
        placement.viewportAnchor === 'center' && placement.unit == null
          ? offsetX
          : solveWorldXForPixelX(camera, baseX, group.position.y, baseZ, desiredPixelX, viewportWidth)
      const solvedY = resolveAvatarAnchorOffsetY({
        camera,
        group,
        avatarAnchor: placement.avatarAnchor,
        offsetY,
        viewportHeight,
        unit: placement.unit,
      })

      if (solvedX == null || solvedY == null) return undefined

      return {
        ...nextAvatarPresentation,
        placement: {
          ...placement,
          offset: {
            ...offset,
            x: solvedX,
            y: solvedY,
          },
        },
      }
    },
    [avatarPresentation, camera, groupRef, size.height, size.width]
  )
}
